import { Facebook, Instagram, Mail, Phone, MapPin, ChevronUp, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContactModal } from '../contexts/ContactModalContext';

const INSTAGRAM_URL = 'https://www.instagram.com/outpostcustom/';
const FACEBOOK_URL = 'https://www.facebook.com/outpostcustom';
const WHATSAPP_URL = 'https://wa.me/message/J2UIKOH5PGSEM1';

/** lucide has no WhatsApp glyph, so the mark is drawn inline. */
const WhatsAppIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const Footer = () => {
  const { openContactModal } = useContactModal();

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const links: Array<{ label: string; to?: string; href?: string; onClick?: () => void }> = [
    { label: 'Contact us', onClick: openContactModal },
    { label: 'About Outpost Custom', to: '/#hero' },
    { label: 'Our work', to: '/#our-work' },
    { label: 'Facebook', href: FACEBOOK_URL },
    { label: 'Instagram', href: INSTAGRAM_URL },
    { label: 'Privacy policy', to: '/privacy-policy' },
  ];

  return (
    <footer
      className="relative w-full bg-[#111111] bg-cover bg-center text-white font-poppins"
      style={{ backgroundImage: 'url(/BlackTextureBackground.webp)' }}
    >
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative">
        <div className="flex justify-center">
          <button
            type="button"
            onClick={scrollTop}
            aria-label="Back to top"
            className="-mt-4 w-9 h-9 bg-black/80 border border-white/10 flex items-center justify-center hover:bg-[#78BE20] transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>

        <div className="max-w-5xl mx-auto px-6 pt-16 pb-12 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <img src="/home/oc-onblack.png" alt="Outpost Custom. Signage, print, clothing" className="h-28 w-auto mb-8" loading="lazy" />
            <ul className="space-y-2 text-[13px] text-white/70">
              <li className="flex gap-3">
                <MapPin className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-white/50" />
                <span>
                  23 (Unit 3) Lisle Avenue
                  <br />
                  Kidderminster
                  <br />
                  DY11 7DF
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="w-3.5 h-3.5 text-white/50" />
                <a href="tel:01562227117" className="hover:text-white transition-colors">01562 227117</a>
              </li>
              <li className="flex gap-3 items-center">
                <WhatsAppIcon className="w-3.5 h-3.5 text-white/50" />
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  WhatsApp @outpostcustom
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="w-3.5 h-3.5 text-white/50" />
                <a href="mailto:info@outpostcustom.co.uk" className="hover:text-white transition-colors">
                  info@outpostcustom.co.uk
                </a>
              </li>
            </ul>
            <div className="mt-8 flex gap-4">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/60 hover:text-[#78BE20] transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white/60 hover:text-[#78BE20] transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-white/60 hover:text-[#78BE20] transition-colors">
                <WhatsAppIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          <ul className="space-y-2 text-[13px] text-white/70 md:pt-2">
            {links.map((l) => {
              const inner = (
                <>
                  <ChevronRight className="w-3 h-3 text-white/40" />
                  {l.label}
                </>
              );
              const cls = 'inline-flex items-center gap-2 hover:text-white transition-colors';
              return (
                <li key={l.label}>
                  {l.onClick ? (
                    <button type="button" onClick={l.onClick} className={cls}>{inner}</button>
                  ) : l.href ? (
                    <a href={l.href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
                  ) : (
                    <Link to={l.to!} className={cls}>{inner}</Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="max-w-5xl mx-auto px-6">
          <div className="border-t border-[#78BE20]/60 pt-6 pb-8 text-center">
            <p className="text-[12px] font-semibold">Outpost Custom Ltd.</p>
            <p className="text-[9px] uppercase tracking-[0.05em] text-white/50 mt-1">
              Company number: 10879647. Registered address: 23 (Unit 3) Lisle Avenue, Kidderminster,
              Worcestershire, England, DY11 7DF. Copyright Outpost Custom {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
